/* eslint-disable react/require-default-props */
import React, { memo, useEffect, useState, ChangeEvent } from 'react';
import { Grid } from '@material-ui/core';

import api from 'services/api';
import onlyNumbers from 'helpers/onlyNumbers';

import Modal from 'components/Modal';
import Text from 'components/Text';
import Button from 'components/Button';
import { Input } from 'components/Fields';

import Spinner from 'components/Spinner';
import Icon from 'components/Icon';

import {
  Container,
  FieldContainer,
  ButtonsContainer,
  ContainerLoader,
  ContainerSuccess,
  ButtonsWrapp,
} from './styles';

enum ResponseType {
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}

interface ISeason {
  description: string;
  year: number;
}

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: (data: any) => void;
  nameComes?: string;
}

const ModalCreateSeason: React.FC<IProps> = ({
  isOpen,
  onClose,
  onSaved,
  nameComes,
}) => {
  const [seasonName, setSeasonName] = useState('');
  const [seasonYear, setSeasonYear] = useState(0);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<any>(false);
  const [error, setError] = useState([]);

  const handleChangeYear = ({
    target: { value },
  }: ChangeEvent<HTMLInputElement>) => {
    const data = onlyNumbers(value);
    setSeasonYear(data);
  };

  useEffect(() => {
    if (nameComes) setSeasonName(nameComes);
    else setSeasonName('');
    setSeasonYear(0);
    setSuccess(false);
    setError([]);
  }, [isOpen]);

  useEffect(() => {
    if (error) {
      setError([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seasonName, seasonYear]);

  const createSeason = async (data: ISeason) => {
    setLoading(true);
    try {
      const seasonResponse = await api.post(`/seasons`, data);
      setLoading(false);
      setSuccess(ResponseType.SUCCESS);
      onSaved(seasonResponse);
    } catch (err: any) {
      if (err?.data?.message) setError(err.data.message);
      else setSuccess(ResponseType.ERROR);
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const closeAndFinish = () => {
    setSuccess(false);
    onClose();
  };

  const isCompleted = seasonName && seasonYear && !error[0];

  return (
    <Modal
      minWidth="1280px"
      minHeight="480px"
      isOpen={isOpen}
      onClose={onClose}
      noButtonClose
    >
      <>
        {!loading ? (
          <Container>
            {!success ? (
              <>
                <Text fontSize={24} color="black">
                  Create new season
                </Text>
                <FieldContainer>
                  <Grid item md={6}>
                    <Input
                      name="season-name"
                      label="Season name"
                      onChange={({ target: { value } }) => setSeasonName(value)}
                      placeholder="Click to add"
                      width="451px"
                      value={seasonName}
                      dataTest="season-name"
                    />
                  </Grid>
                  <Grid item md={6}>
                    <Input
                      name="season-year"
                      label="Season year"
                      onChange={handleChangeYear}
                      placeholder="Click to add"
                      width="451px"
                      value={seasonYear || ''}
                      dataTest="season-year"
                      error={error[0]}
                    />
                  </Grid>
                </FieldContainer>
                <ButtonsContainer>
                  <Button
                    onClick={() =>
                      isCompleted &&
                      createSeason({
                        description: seasonName,
                        year: seasonYear,
                      })
                    }
                    width="266px"
                    data-testid="button-create"
                    scheme={!isCompleted ? 'disabled' : 'default'}
                  >
                    Create
                  </Button>
                  <Button
                    data-testid="button-cancel"
                    width="266px"
                    scheme="nobg"
                    onClick={onClose}
                  >
                    Cancel
                  </Button>
                </ButtonsContainer>
              </>
            ) : (
              <>
                {success === ResponseType.SUCCESS && (
                  <ContainerSuccess>
                    <Icon icon="check-circle" color="none" width="68px" />
                    <Text fontSize={24} fontWeight="normal-bold" color="black">
                      Season created with success!
                    </Text>

                    <Text
                      fontSize={18}
                      fontWeight="medium"
                      color="black"
                      margin="15px 0 0"
                    >
                      The season{' '}
                      <b>
                        {seasonName} {seasonYear}
                      </b>{' '}
                      has been added to the system.
                    </Text>

                    <Button
                      data-testid="button-ok"
                      width="250px"
                      onClick={() => closeAndFinish()}
                    >
                      Ok
                    </Button>
                  </ContainerSuccess>
                )}

                {success === ResponseType.ERROR && (
                  <ContainerSuccess>
                    <Icon icon="close-error" color="none" width="68px" />
                    <Text color="black" fontSize={30}>
                      Could not create this season at the moment
                    </Text>
                    <Text
                      color="black"
                      fontSize={18}
                      fontWeight="normal"
                      margin="15px 0 0"
                    >
                      We&rsquo;re sorry for the inconvenience. Please, try again
                      later
                    </Text>

                    <ButtonsWrapp>
                      <Button
                        width="250px"
                        onClick={() =>
                          createSeason({
                            description: seasonName,
                            year: seasonYear,
                          })
                        }
                      >
                        Try again
                      </Button>
                      <Button
                        width="250px"
                        onClick={() => closeAndFinish()}
                        scheme="nobg"
                      >
                        Back
                      </Button>
                    </ButtonsWrapp>
                  </ContainerSuccess>
                )}
              </>
            )}
          </Container>
        ) : (
          <ContainerLoader>
            <Text fontSize={24} color="black">
              Creating season
              <Spinner />
            </Text>
          </ContainerLoader>
        )}
      </>
    </Modal>
  );
};

export default memo(ModalCreateSeason);
